import { createElement, useMemo } from 'react';

import { toPersianDigits } from '@bookora/shared';
import { Icon, findAnchor, type MenuProps } from '@bookora/ui';

import { useGuidance } from './api';
import { useStepContext } from './context';
import { resumeIndexOf, tourStatus } from './eligibility';
import { useTourFeatures } from './features';
import { helpNoteFor } from './help-notes';
import { useHubBadgeTotal } from './hub-sections';
import { resolveSteps, tourAllowed } from './resolve-steps';
import { openGuideHub, openHelpNotes, startTour } from './runtime';
import { RUNTIME_COPY } from './runtime-copy';
import { findItem } from './state';
import { sectionKeyOf, useSectionHot } from './struggle/store';
import { useTourStore } from './tour-store';
import { COPY } from './tours/copy';
import { TOURS, tourForPath } from './tours/registry';

export type HelpMenuItem = NonNullable<MenuProps['items']>[number];

/** The launcher focus returns to: the header «راهنما» (wide) or the account menu (compact). */
function helpLauncher(viewport: 'wide' | 'compact'): HTMLElement | null {
  return findAnchor(viewport === 'compact' ? 'shell.user-menu' : 'shell.help');
}

/**
 * The Help menu's items, shared with the compact user menu's «راهنما» group.
 * Each item carries its own `onClick`, so a host menu with its own click
 * handler (the user menu) needs no knowledge of them. «تماس با پشتیبانی» is
 * not here: the wide menu adds it, and the user menu already has it.
 *
 *  · «پیشنهادهای جدید (n)» FIRST while the Help button wears its dot — the dot
 *    has to lead somewhere — opening the hub on «پیشنهادها».
 *  · «راهنمای این صفحه» when the page (path AND query: `/settings?tab=hours`)
 *    has a tour for this viewer; «ادامهٔ راهنمای این صفحه» resumes at the
 *    step an interrupted run stopped on.
 *  · Otherwise «کمک برای همین صفحه»: the page's help notes — «کمک برای همین
 *    قسمت» when the person seems stuck here (critic C6).
 *  · «آشنایی با پنل», then the hub.
 */
export function useHelpMenuItems(): HelpMenuItem[] {
  const ctx = useStepContext();
  const features = useTourFeatures();
  const pathname = useTourStore((s) => s.pathname);
  const search = useTourStore((s) => s.search);
  const guidance = useGuidance();
  const badge = useHubBadgeTotal();
  const section = sectionKeyOf(pathname);
  const hot = useSectionHot(section);
  const data = guidance.data;

  return useMemo(() => {
    const items: HelpMenuItem[] = [];
    if (badge > 0) {
      items.push({
        key: 'guidance-new-tips',
        icon: createElement(Icon, { name: 'sparkle' }),
        // A single number between words is bidi-safe; the chip is the accent
        // family, never a danger badge.
        label: createElement(
          'span',
          null,
          RUNTIME_COPY.menu.newTips,
          createElement('span', { className: 'bk-help-menu__count' }, toPersianDigits(badge)),
        ),
        onClick: () => openGuideHub({ section: 'tips' }, 'help-menu'),
      });
    }

    const page = tourForPath(pathname, search);
    const pageUsable =
      page !== null &&
      tourAllowed(page, ctx.permissions, features) &&
      resolveSteps(page, ctx, { trim: false }).length >= page.minSteps;
    if (page && pageUsable) {
      const now = new Date();
      const item = findItem(data, 'TOUR', page.key);
      const status = tourStatus(item, page, now);
      items.push({
        key: 'guidance-page',
        icon: createElement(Icon, { name: 'help' }),
        label: status === 'in-progress' ? COPY.help.pageContinue : COPY.help.page,
        onClick: () =>
          startTour(page.key, {
            source: 'help-menu',
            resumeIndex: resumeIndexOf(item, page, now),
            returnFocusTo: helpLauncher(ctx.viewport),
          }),
      });
    } else if (helpNoteFor(section)) {
      items.push({
        key: 'guidance-page-notes',
        icon: createElement(Icon, { name: 'help' }),
        label: hot ? RUNTIME_COPY.menu.pageHelpStuck : RUNTIME_COPY.menu.pageHelp,
        onClick: () => openHelpNotes(section),
      });
    }
    items.push({
      key: 'guidance-core',
      icon: createElement(Icon, { name: 'directions' }),
      label: COPY.help.core,
      onClick: () =>
        startTour(TOURS.core.key, {
          source: 'help-menu',
          returnFocusTo: helpLauncher(ctx.viewport),
        }),
    });
    items.push({
      key: 'guidance-hub',
      icon: createElement(Icon, { name: 'encyclopedia' }),
      label: COPY.help.hub,
      onClick: () => openGuideHub(undefined, 'help-menu'),
    });
    return items;
  }, [pathname, search, ctx, features, data, badge, section, hot]);
}

/** Whether the person seems stuck on this page (the wide menu then lifts «تماس با پشتیبانی»). */
export function useHelpMenuStuck(): boolean {
  const pathname = useTourStore((s) => s.pathname);
  return useSectionHot(sectionKeyOf(pathname));
}
